/** Client API widget Xsavier. */

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || "http://localhost:8000";

let _session: string | null = null;

export function setSession(t: string | null) {
  _session = t;
  try {
    if (t) window.sessionStorage.setItem("xsavier_widget_session", t);
    else window.sessionStorage.removeItem("xsavier_widget_session");
  } catch {
    // sessionStorage pas dispo (ex: iframe sandbox) — on garde juste en mémoire
  }
}

export function getSession(): string | null {
  if (_session) return _session;
  try {
    _session = window.sessionStorage.getItem("xsavier_widget_session");
  } catch {
    // ignore
  }
  return _session;
}

type Opts = { method?: string; body?: unknown; anonymous?: boolean };

async function request<T>(path: string, opts: Opts = {}): Promise<T> {
  const { method = "GET", body, anonymous = false } = opts;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (!anonymous) {
    const s = getSession();
    if (s) headers["Authorization"] = `Bearer ${s}`;
  }
  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await resp.text();
  const data = text ? JSON.parse(text) : null;
  if (!resp.ok) {
    throw new Error(data?.detail || `HTTP ${resp.status}`);
  }
  return data as T;
}

// ── Types ──────────────────────────────────────────────

export type ClientInfo = {
  id: string | null;
  prestashop_customer_id: number;
  email: string;
  full_name: string | null;
  company: string | null;
  language: string;
  cgv_accepted: boolean;
};

export type SAVSummary = {
  id: string;
  reference: string;
  status: string;
  voie: string | null;
  product_name: string | null;
  product_reference: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
};

export type TimelineItem = {
  id: string;
  kind: "client_in" | "xsavier_out" | "status_change" | "system";
  content: string | null;
  created_at: string;
  actor_type: string | null;
};

export type AuthResponse = {
  session_token: string;
  customer_id: number;
  email: string;
};

export type MessageResponse = {
  response_text: string | null;
  agent_phase: "question_to_client" | "final_diagnosis" | "chat_response" | null;
  diagnostic_confidence: number | null;
  decision_id: string | null;
  sav_updated: boolean;
};

export type ProductInOrder = {
  product_id: number;
  product_reference: string | null;
  product_name: string | null;
  product_quantity: number;
  unit_price_tax_excl: number;
  weight_kg: number | null;
  age_months: number | null;
};

export type OrderItem = {
  id_order: number;
  reference: string | null;
  total_paid_tax_excl: number;
  invoice_date: string | null;
  date_add: string | null;
  products: ProductInOrder[];
};

export type OrdersResponse = {
  orders: OrderItem[];
  prestashop_enabled: boolean;
};

// ── Endpoints ─────────────────────────────────────────

export const API = {
  auth: (prestashop_token: string) =>
    request<AuthResponse>("/api/widget/auth", {
      method: "POST",
      body: { prestashop_token },
      anonymous: true,
    }),
  clientInfo: () => request<ClientInfo>("/api/widget/client/info"),
  listSAVs: () =>
    request<{ in_progress: SAVSummary[]; closed: SAVSummary[] }>("/api/widget/client/savs"),
  acceptCGV: (version = "v1") =>
    request<{ status: string }>("/api/widget/client/cgv-accept", {
      method: "POST",
      body: { version },
    }),
  getPrestashopOrders: () =>
    request<OrdersResponse>("/api/widget/client/prestashop-orders"),
  createSAV: (data: {
    prestashop_order_id?: number;
    prestashop_product_id?: number;
    product_reference?: string;
    product_name?: string;
    product_weight_kg?: string;
    purchase_price_ht?: string;
    panne_description: string;
  }) => request<SAVSummary>("/api/widget/sav", { method: "POST", body: data }),
  getSAV: (id: string) => request<SAVSummary>(`/api/widget/sav/${id}`),
  getTimeline: (id: string) => request<TimelineItem[]>(`/api/widget/sav/${id}/timeline`),
  sendMessage: (id: string, content: string) =>
    request<MessageResponse>(`/api/widget/sav/${id}/messages`, {
      method: "POST",
      body: { content },
    }),
};
